import { Badge } from "@/components/ui/badge";
import { Lock, Clock, CheckCircle, XCircle } from "lucide-react";
import { getSportEmoji, isFixtureLocked, getCountdown } from "@/lib/fixtures";
import { useSlip, UCL_LOGO, KCL_LOGO } from "@/components/Layout";

export default function FixtureCard({ fixture, compact = false, prediction = null, result = null }) {
  const { addToSlip, isItemInSlip } = useSlip();
  const locked = isFixtureLocked(fixture);
  const countdown = getCountdown(fixture);
  const slipItem = isItemInSlip(fixture.id);
  const emoji = getSportEmoji(fixture.sport);
  const hasResult = result && result.winner;

  const handlePick = (team) => {
    if (locked || prediction) return;
    addToSlip(fixture.id, team, fixture);
  };

  // Result badge for the fixture
  const resultBadge = hasResult ? (
    <Badge className="text-[10px] bg-white/5 text-white border-white/10 gap-1 font-bold">
      {result.winner === "A" ? "UCL" : "KCL"} won
    </Badge>
  ) : null;

  // Prediction outcome badge
  const predBadge = (pred) => {
    if (!pred) return null;
    if (pred.status === "won") return (
      <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30 gap-1">
        <CheckCircle size={8} /> CORRECT +10
      </Badge>
    );
    if (pred.status === "lost") return (
      <Badge className="text-[10px] bg-red-500/10 text-red-400 border-red-500/30 gap-1">
        <XCircle size={8} /> INCORRECT
      </Badge>
    );
    return (
      <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30">
        {pred.team === "A" ? "UCL" : "KCL"} picked
      </Badge>
    );
  };

  if (compact) {
    return (
      <div className={`match-row ${locked ? "opacity-50" : ""}`} data-testid={`fixture-row-${fixture.id}`}>
        <span className="text-base mr-3">{emoji}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-white truncate">{fixture.sport}</p>
          <p className="text-[10px] text-slate-500 font-mono">{fixture.date} {fixture.time}</p>
        </div>
        {hasResult && <span className="mr-2">{resultBadge}</span>}
        {locked && !prediction ? (
          <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400 gap-1 mr-3"><Lock size={8} /> LOCKED</Badge>
        ) : prediction ? (
          <span className="mr-3">{predBadge(prediction)}</span>
        ) : (
          <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1 font-mono mr-3"><Clock size={8} /> {countdown}</Badge>
        )}
        {!prediction && !locked && (
          <div className="flex gap-1.5">
            <button
              data-testid={`fixture-${fixture.id}-pick-ucl`}
              disabled={locked || !!prediction}
              onClick={() => handlePick("A")}
              className={`pick-btn text-xs rounded-sm ${slipItem?.team === "A" ? "selected" : ""}`}
            >
              <img src={UCL_LOGO} alt="UCL" className="w-4 h-4 object-contain" /> UCL
            </button>
            <button
              data-testid={`fixture-${fixture.id}-pick-kcl`}
              disabled={locked || !!prediction}
              onClick={() => handlePick("B")}
              className={`pick-btn text-xs rounded-sm ${slipItem?.team === "B" ? "selected" : ""}`}
            >
              <img src={KCL_LOGO} alt="KCL" className="w-4 h-4 object-contain" /> KCL
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-[#111827] border border-white/5 rounded-sm overflow-hidden transition-all hover:border-white/10 ${locked ? "opacity-50" : ""}`} data-testid={`fixture-card-${fixture.id}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{emoji}</span>
            <span className="text-xs font-semibold uppercase tracking-wider text-white">{fixture.sport}</span>
          </div>
          <div className="flex items-center gap-2">
            {hasResult && resultBadge}
            {locked ? (
              <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400 gap-1"><Lock size={9} /> LOCKED</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] border-white/10 text-slate-400 gap-1 font-mono"><Clock size={9} /> {countdown}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <button
            data-testid={`fixture-${fixture.id}-pick-ucl-card`}
            disabled={locked || !!prediction}
            onClick={() => handlePick("A")}
            className={`pick-btn flex-1 py-4 rounded-sm flex-col ${prediction?.team === "A" ? "selected" : slipItem?.team === "A" ? "selected" : ""} ${hasResult && result.winner === "A" ? "border-green-500/50 bg-green-500/10" : ""}`}
          >
            <img src={UCL_LOGO} alt="UCL" className="w-10 h-10 object-contain mb-1" />
            <span className="text-sm font-bold">UCL</span>
            {hasResult && result.winner === "A" && <span className="text-[9px] text-green-400 mt-0.5">Winner</span>}
          </button>
          <div className="flex flex-col items-center px-2">
            <span className="text-[10px] text-slate-600 uppercase tracking-widest">vs</span>
            <span className="text-[10px] text-slate-500 font-mono mt-1">{fixture.time}</span>
            <span className="text-[10px] text-slate-600 font-mono">{fixture.date.split(" ").slice(0, 2).join(" ")}</span>
          </div>
          <button
            data-testid={`fixture-${fixture.id}-pick-kcl-card`}
            disabled={locked || !!prediction}
            onClick={() => handlePick("B")}
            className={`pick-btn flex-1 py-4 rounded-sm flex-col ${prediction?.team === "B" ? "selected" : slipItem?.team === "B" ? "selected" : ""} ${hasResult && result.winner === "B" ? "border-green-500/50 bg-green-500/10" : ""}`}
          >
            <img src={KCL_LOGO} alt="KCL" className="w-10 h-10 object-contain mb-1" />
            <span className="text-sm font-bold">KCL</span>
            {hasResult && result.winner === "B" && <span className="text-[9px] text-green-400 mt-0.5">Winner</span>}
          </button>
        </div>
        {prediction && (
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">
              Prediction: <span className="text-white font-semibold">{prediction.team === "A" ? "UCL" : "KCL"}</span>
            </span>
            {predBadge(prediction)}
          </div>
        )}
      </div>
    </div>
  );
}
